import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Tab, Tabs } from '@mui/material';
import AppLayout from '../../layout/app.layout';
import { viewDataById } from '../../services/distributor.service';
import ROUTE_PATH from '../../config/routes.config';
import Loading from '../../components/table-elements/loading.component';
import SnackbarInfo from '../../components/common/snackbar-info.component';
import PageNotFoundView from '../errors/not-found.view';
import ConfirmPopup from '../../components/common/confirm-popup.component';
import ContentOwnerBasicInfo from '../../components/content-owner/content-owner-basic-info.component';
import ContentOwnerLogin from '../../components/content-owner/content-owner-login.component';
import ContentOwnerVideoAssignment from '../../components/content-owner/content-owner-video-assignment.component';
import replaceIdUrl from '../../utils/url.util';
import Iconify from '../../components/common/iconify.component';

const ContentOwnerAddEditView = ({ editMode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tabsConfig, setTabsConfig] = useState([
    {
      value: 'contentOwnerBasicInfo',
      label: 'Basic Info',
      component: ContentOwnerBasicInfo,
    },
  ]);
  const [tabValue, setTabValue] = useState(tabsConfig[0].value);
  const [loading, setLoading] = useState(true);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [showPageNotFound, setShowPageNotFound] = useState(false);
  const [distributorDetail, setDistributorDetail] = useState({});
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [hasFormValueChanged, setHasFormValueChanged] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const tabValueRef = useRef('');

  const formValueChangedCallback = (val) => {
    setHasFormValueChanged(val);
  };

  const handleRefreshData = () => {
    setReloadCounter(reloadCounter + 1);
  };

  const handleTabSwitchSuccess = () => {
    setTabValue(tabValueRef.current);
    setHasFormValueChanged(false);
    setShowWarning(false);
  };

  useEffect(() => {
    if (!editMode) {
      setLoading(false);
      return;
    }

    const baseTabs = [
      {
        value: 'contentOwnerBasicInfo',
        label: 'Basic Info',
        component: ContentOwnerBasicInfo,
      },
      {
        value: 'contentOwnerLogin',
        label: 'Login',
        component: ContentOwnerLogin,
      },
      {
        value: 'contentOwnerManageVideos',
        label: 'Manage Videos',
        component: ContentOwnerVideoAssignment,
      },
    ];
    setTabsConfig(baseTabs);

    viewDataById(id)
      .then((res) => {
        setDistributorDetail(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response.status === 404) {
          setShowPageNotFound(true);
          return;
        }
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [id, reloadCounter]);

  return editMode && (Number.isNaN(Number(id)) || showPageNotFound) ? (
    <PageNotFoundView />
  ) : (
    <AppLayout
      pageTitle={
        editMode
          ? `Edit Distributor Details - ${distributorDetail.email}`
          : 'Create New Distributor'
      }
      showBack
      handleBack={() => navigate(ROUTE_PATH.CONTENT_OWNERS)}
    >
      {editMode && (
        <Box
          display="flex"
          justifyContent="flex-end"
          my={1}
          width={
            tabValue === 'contentOwnerManageVideos'
              ? 'calc(100% - 250px)'
              : '100%'
          }
        >
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="lets-icons:view" />}
            onClick={() => {
              const url = replaceIdUrl(
                ROUTE_PATH.CONTENT_OWNERS_VIEW_DETAILS,
                id
              );
              navigate(url);
            }}
            disabled={loading}
          >
            View Distributor
          </Button>
        </Box>
      )}

      <Box height="100%" display="flex">
        <Tabs
          value={tabValue}
          aria-label="tabs"
          orientation="vertical"
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="transparent"
          sx={{ minWidth: 150, maxWidth: 150 }}
          onChange={(_, v) => {
            if (hasFormValueChanged) {
              tabValueRef.current = v;
              setShowWarning(true);
            } else {
              setTabValue(v);
            }
          }}
        >
          {tabsConfig.map((tab, index) => (
            <Tab
              key={index + 1}
              value={tab.value}
              label={tab.label}
              sx={{
                background: tabValue === tab.value && '#fff',
                borderLeft: tabValue === tab.value && '2px solid #FD0100',
                alignItems: 'flex-start',
                textAlign: 'left',
              }}
            />
          ))}
        </Tabs>

        <Box flexGrow={1}>
          {loading && <Loading />}

          {!loading && (
            <>
              {tabsConfig.map(({ value, component: Component }) =>
                tabValue === value ? (
                  <Component
                    key={value}
                    distributorDetail={distributorDetail}
                    handleRefreshData={handleRefreshData}
                    formValueChangedCallback={formValueChangedCallback}
                    editMode={editMode}
                  />
                ) : null
              )}
            </>
          )}
        </Box>
      </Box>

      {showWarning && (
        <ConfirmPopup
          title="Tab Switch Warning"
          message="Do you want to switch tab without saving data? Before switching tab, save your changes otherwise you lost all your changes and can not undo this action!"
          onCancel={() => setShowWarning(false)}
          onSuccess={() => handleTabSwitchSuccess()}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </AppLayout>
  );
};

ContentOwnerAddEditView.propTypes = {
  editMode: PropTypes.bool.isRequired,
};

export default ContentOwnerAddEditView;
